import { useCallback, useEffect, useState } from "react";
import {
  Scale, RefreshCw, Loader2, CheckCircle2, AlertTriangle, ShieldQuestion,
  ChevronDown, ChevronRight, History, Info, Lock,
} from "lucide-react";
import { useTenant } from "@/context/TenantContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { DueDiligencePanel } from "@/components/DueDiligencePanel";

const DISCLAIMER = "ESG PMI Score è una metodologia proprietaria ESG-PMI e non costituisce rating creditizio, certificazione ESG o attestazione ufficiale VSME/MEF.";

const RUN_STATUS = {
  NOT_CALCULATED: { label: "Non calcolato", cls: "bg-slate-100 text-slate-600" },
  INSUFFICIENT_DATA: { label: "Dati insufficienti", cls: "bg-red-100 text-red-700" },
  PROVISIONAL: { label: "Provvisorio", cls: "bg-amber-100 text-amber-800" },
  CALCULATED: { label: "Calcolato", cls: "bg-sky-100 text-sky-800" },
  UNDER_REVIEW: { label: "In revisione", cls: "bg-indigo-100 text-indigo-800" },
  APPROVED: { label: "Approvato", cls: "bg-green-100 text-green-800" },
  LOCKED: { label: "Bloccato", cls: "bg-slate-200 text-slate-700" },
  SUPERSEDED: { label: "Sostituito", cls: "bg-slate-100 text-slate-500" },
  CALCULATING: { label: "Calcolo in corso…", cls: "bg-sky-100 text-sky-800" },
  FAILED: { label: "Calcolo non riuscito", cls: "bg-red-100 text-red-700" },
};

// Stato VISUALE derivato dal backend (Macro-Area A): unica etichetta mostrata sullo score corrente.
const DISPLAY_STATUS = {
  CURRENT: { label: "Aggiornato", cls: "bg-green-100 text-green-800" },
  STALE: { label: "Da ricalcolare", cls: "bg-amber-100 text-amber-800" },
  INSUFFICIENT_DATA: { label: "Dati insufficienti", cls: "bg-red-100 text-red-700" },
  CALCULATING: { label: "Calcolo in corso…", cls: "bg-sky-100 text-sky-800" },
  FAILED: { label: "Calcolo non riuscito", cls: "bg-red-100 text-red-700" },
};

const CONFIDENCE = {
  HIGH: { label: "Alta", cls: "bg-green-100 text-green-800" },
  MEDIUM: { label: "Media", cls: "bg-amber-100 text-amber-800" },
  LOW: { label: "Bassa", cls: "bg-red-100 text-red-700" },
};

const DIM_LABELS = { E: "Environmental", S: "Social", G: "Governance" };
const DQ_LABELS = {
  completeness: "Completezza", evidence: "Evidenze", verification: "Verifica",
  consistency: "Coerenza", traceability: "Tracciabilità", freshness: "Aggiornamento",
  source_quality: "Qualità delle fonti",
};

function ScoreCard({ title, value, subtitle, testid, accent, badge }) {
  return (
    <Card className="border-slate-200" data-testid={testid}>
      <CardContent className="p-5">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{title}</p>
        <div className="flex items-end gap-2 mt-1">
          <p className={`text-4xl font-extrabold ${accent || "text-slate-900"}`} data-testid={`${testid}-value`}>
            {value ?? "—"}
          </p>
          {value != null && <span className="text-sm text-slate-400 mb-1">/100</span>}
          {badge}
        </div>
        {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
      </CardContent>
    </Card>
  );
}

function Bar({ value, testid }) {
  return (
    <div className="h-2.5 rounded-full bg-slate-100" role="progressbar" aria-valuenow={value ?? 0}
      aria-valuemin={0} aria-valuemax={100} data-testid={testid}>
      <div className="h-full rounded-full bg-green-700 transition-all" style={{ width: `${value ?? 0}%` }} />
    </div>
  );
}

export default function Valutazione() {
  const { companyId, period, can } = useTenant();
  const canApprove = can("score:approve");
  const [current, setCurrent] = useState(undefined);
  const [loadError, setLoadError] = useState(null);
  const [dq, setDq] = useState(null);
  const [history, setHistory] = useState([]);
  const [detail, setDetail] = useState(null);
  const [openDim, setOpenDim] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [approveNote, setApproveNote] = useState("");
  const [showApprove, setShowApprove] = useState(false);

  const load = useCallback(async () => {
    if (!companyId || !period?.id) return;
    try {
      const { data } = await api.get(
        `/companies/${companyId}/periods/${period.id}/score-runs/current`);
      setCurrent(data);
      setLoadError(null);
      if (data.run?.id) {
        const det = await api.get(
          `/companies/${companyId}/periods/${period.id}/score-runs/${data.run.id}`);
        setDetail(det.data);
      } else {
        setDetail(null);
      }
    } catch (e) {
      setLoadError(formatApiError(e));
      setCurrent(null);
    }
    api.get(`/companies/${companyId}/periods/${period.id}/data-quality`)
      .then((r) => setDq(r.data)).catch(() => setDq(null));
    api.get(`/companies/${companyId}/periods/${period.id}/score-runs/history`)
      .then((r) => setHistory(r.data.runs || [])).catch(() => setHistory([]));
  }, [companyId, period?.id]);

  useEffect(() => { load(); }, [load]);

  // Se l'ultimo run è in corso di calcolo, aggiorna automaticamente lo stato.
  useEffect(() => {
    if (current?.run?.display_status !== "CALCULATING") return undefined;
    const timer = setTimeout(load, 4000);
    return () => clearTimeout(timer);
  }, [current?.run?.display_status, load]);

  const calculate = async () => {
    setBusy(true);
    try {
      await api.post(`/companies/${companyId}/periods/${period.id}/score-runs/calculate`);
      toast.success("Valutazione calcolata");
      setCurrent(undefined);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const approve = async () => {
    setBusy(true);
    try {
      await api.post(
        `/companies/${companyId}/periods/${period.id}/score-runs/${current.run.id}/approve`,
        { note: approveNote || null });
      toast.success("Score approvato");
      setShowApprove(false);
      setApproveNote("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const run = current?.run;
  const expl = detail?.explanation;
  const disp = run?.display_status ? DISPLAY_STATUS[run.display_status] : null;
  const insufficient = run?.display_status === "INSUFFICIENT_DATA";
  const conf = run?.score_confidence ? CONFIDENCE[run.score_confidence] : null;

  return (
    <div className="space-y-6" data-testid="valutazione-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">Valutazione ESG</h1>
          <p className="text-sm text-slate-600 mt-1">
            ESG PMI Score e Data Quality Score — <strong>Esercizio {period?.year}</strong>. Area riservata al consulente.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {run && (
            <button type="button" onClick={() => setShowHistory((v) => !v)}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700 bg-white border border-slate-300 hover:bg-slate-100 rounded-md px-3 py-2 transition-colors"
              data-testid="toggle-history-button">
              <History className="w-4 h-4" aria-hidden="true" /> Storico
            </button>
          )}
          <button type="button" onClick={calculate} disabled={busy}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-green-700 hover:bg-green-800 disabled:opacity-50 rounded-md px-4 py-2 transition-colors"
            data-testid="calculate-score-button">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <RefreshCw className="w-4 h-4" aria-hidden="true" />}
            {run ? "Ricalcola" : "Calcola valutazione"}
          </button>
        </div>
      </div>

      <p className="text-xs text-slate-500 flex items-start gap-1.5" data-testid="score-disclaimer">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden="true" />
        {DISCLAIMER}
      </p>

      {loadError ? (
        <Card className="border-red-200 bg-red-50" data-testid="score-load-error">
          <CardContent className="p-4 text-sm text-red-700">{loadError}</CardContent>
        </Card>
      ) : !current ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4 animate-pulse"
          data-testid="score-loading" aria-busy="true" aria-live="polite">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="rounded-lg border border-slate-200 bg-white p-5 space-y-3">
              <div className="h-3 w-24 rounded bg-slate-100" />
              <div className="h-9 w-16 rounded bg-slate-200" />
              <div className="h-3 w-32 rounded bg-slate-100" />
            </div>
          ))}
        </div>
      ) : !run ? (
        <Card className="border-slate-200" data-testid="score-empty">
          <CardContent className="p-8 text-center">
            <Scale className="w-8 h-8 text-slate-300 mx-auto mb-3" aria-hidden="true" />
            <p className="text-sm text-slate-600 font-medium">Nessuna valutazione calcolata per questo esercizio.</p>
            <p className="text-xs text-slate-500 mt-1">Calcola la valutazione quando i dati canonici del periodo sono pronti.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Score cards (punto 85): due card separate, sempre distinte */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
            <ScoreCard title="ESG PMI Score" value={insufficient ? null : run.overall_score} testid="overall-score"
              subtitle={detail?.explanation?.band || run.band}
              badge={disp && (
                <span className={`text-xs font-semibold px-2 py-0.5 rounded mb-1 ${disp.cls}`} data-testid="score-status">
                  {disp.label}
                </span>)} />
            {["E", "S", "G"].map((d) => (
              <ScoreCard key={d} title={DIM_LABELS[d]} testid={`score-dim-${d}`}
                value={insufficient ? null : run[{ E: "environmental_score", S: "social_score", G: "governance_score" }[d]]}
                subtitle={run.coverage_by_dimension?.[d] != null
                  ? `copertura ${run.coverage_by_dimension[d]}%` : undefined} />
            ))}
            <ScoreCard title="Data Quality" value={run.data_quality_score ?? dq?.score}
              testid="score-dq" accent="text-indigo-800"
              subtitle="Solidità dei dati, non performance ESG"
              badge={conf && (
                <span className={`text-xs font-semibold px-2 py-0.5 rounded mb-1 ${conf.cls}`} data-testid="score-confidence">
                  Fiducia {conf.label}
                </span>)} />
          </div>

          {run.stale && (
            <Card className="border-amber-300 bg-amber-50" data-testid="score-stale-banner">
              <CardContent className="p-4 flex items-center gap-2 text-sm text-amber-800">
                <AlertTriangle className="w-4 h-4 shrink-0" aria-hidden="true" />
                I dati sono cambiati dopo questo calcolo: lo score è <strong>da ricalcolare</strong>.
                {run.status === "APPROVED" && " Il run approvato resta immutato fino a nuova approvazione."}
              </CardContent>
            </Card>
          )}
          {run.status === "INSUFFICIENT_DATA" && (
            <Card className="border-red-200 bg-red-50" data-testid="score-insufficient-banner">
              <CardContent className="p-4 text-sm text-red-700">
                Dati insufficienti per una valutazione affidabile. Copertura complessiva: {run.coverage_percentage}%.
              </CardContent>
            </Card>
          )}
          {run.status === "FAILED" && (
            <Card className="border-red-200 bg-red-50" data-testid="score-failed-banner">
              <CardContent className="p-4 text-sm text-red-700">
                L'ultimo calcolo non è andato a buon fine{run.error_message ? `: ${run.error_message}` : ""}.
                Riprova con "Ricalcola".
              </CardContent>
            </Card>
          )}
          {run.display_status === "CALCULATING" && (
            <Card className="border-sky-200 bg-sky-50" data-testid="score-calculating-banner">
              <CardContent className="p-4 text-sm text-sky-800 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin shrink-0" aria-hidden="true" />
                Calcolo in corso: la pagina si aggiorna automaticamente tra pochi secondi…
              </CardContent>
            </Card>
          )}

          {/* Drivers deterministici (punti 95-98) — nascosti se i dati non bastano per una valutazione affidabile */}
          {expl && !insufficient && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="border-slate-200" data-testid="positive-drivers">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-slate-700">Principali driver positivi</CardTitle>
                </CardHeader>
                <CardContent>
                  {expl.positive_drivers?.length ? (
                    <ul className="space-y-1.5">
                      {expl.positive_drivers.map((d) => (
                        <li key={d.metric_code} className="text-sm text-slate-700 flex justify-between gap-2">
                          <span>{d.label}</span>
                          <span className="font-semibold text-green-800">{d.metric_score}/100</span>
                        </li>
                      ))}
                    </ul>
                  ) : <p className="text-sm text-slate-500">Nessun driver positivo rilevante.</p>}
                </CardContent>
              </Card>
              <Card className="border-slate-200" data-testid="attention-areas">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-slate-700">Principali aree di attenzione</CardTitle>
                </CardHeader>
                <CardContent>
                  {expl.attention_areas?.length ? (
                    <ul className="space-y-1.5">
                      {expl.attention_areas.map((d) => (
                        <li key={d.metric_code} className="text-sm text-slate-700 flex justify-between gap-2">
                          <span>{d.label}{d.reason === "DATA_MISSING" && " (dato mancante)"}</span>
                          <span className="font-semibold text-amber-800">
                            {d.metric_score != null ? `${d.metric_score}/100` : "—"}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : <p className="text-sm text-slate-500">Nessuna area di attenzione rilevante.</p>}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Contribution tree per dimensione (punti 61-62, 92-94) — nascosto con INSUFFICIENT_DATA */}
          {expl && !insufficient && (
            <Card className="border-slate-200" data-testid="contribution-tree">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-700">Come è stato ottenuto il punteggio</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {["E", "S", "G"].map((d) => {
                  const dim = expl.dimensions[d];
                  const open = openDim === d;
                  return (
                    <div key={d} className="border border-slate-200 rounded-lg" data-testid={`dim-section-${d}`}>
                      <button type="button" onClick={() => setOpenDim(open ? null : d)}
                        className="w-full flex items-center gap-3 p-3 text-left"
                        data-testid={`dim-toggle-${d}`} aria-expanded={open}>
                        {open ? <ChevronDown className="w-4 h-4 text-slate-400" aria-hidden="true" />
                          : <ChevronRight className="w-4 h-4 text-slate-400" aria-hidden="true" />}
                        <span className="text-sm font-semibold text-slate-800 w-32">{DIM_LABELS[d]}</span>
                        <div className="flex-1"><Bar value={dim.score} testid={`dim-bar-${d}`} /></div>
                        <span className="text-sm font-bold text-slate-900 w-16 text-right">{dim.score ?? "—"}/100</span>
                      </button>
                      {open && (
                        <ul className="px-4 pb-3 space-y-1.5 border-t border-slate-100 pt-2">
                          {dim.metrics.map((m) => (
                            <li key={m.metric_code} className="text-xs text-slate-600 flex flex-wrap items-center gap-x-2"
                              data-testid={`metric-${m.metric_code}`}>
                              <span className="font-medium text-slate-800">{m.label}</span>
                              {m.applicability === "NOT_APPLICABLE" ? (
                                <span className="text-slate-400">non applicabile (non influisce)</span>
                              ) : m.metric_score == null ? (
                                <span className="text-amber-700">dato mancante{m.critical_flag && " · critico"}</span>
                              ) : (
                                <>
                                  <span>valore {m.raw_value != null ? String(m.raw_value) : "—"} → {m.metric_score}/100</span>
                                  <span>× peso {m.weight}% = <strong>{m.weighted_contribution}</strong> pt</span>
                                  {m.evidence_status === "PRESENT" && <span className="text-green-700">· con evidenza</span>}
                                </>
                              )}
                            </li>
                          ))}
                          {(expl.adjustments || []).filter((a) => a.dimension === d).map((a) => (
                            <li key={a.code} className="text-xs text-red-700" data-testid={`adjustment-${a.code}`}>
                              {a.label}: −{a.penalty} pt
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
                <p className="text-xs text-slate-500 pt-1">
                  Metodologia {expl.methodology_version} · pesi E/S/G {expl.dimension_weights?.E}/{expl.dimension_weights?.S}/{expl.dimension_weights?.G}%
                </p>
              </CardContent>
            </Card>
          )}

          {/* Data Quality breakdown (punti 82-83, 176) */}
          {dq && dq.score != null && (
            <Card className="border-slate-200" data-testid="dq-breakdown">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <ShieldQuestion className="w-4 h-4" aria-hidden="true" />
                  Data Quality Score: {dq.score}/100
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {Object.entries(DQ_LABELS).map(([k, label]) => (
                    dq.components?.[k] != null && (
                      <div key={k} data-testid={`dq-${k}`}>
                        <div className="flex justify-between text-xs text-slate-600 mb-1">
                          <span>{label}</span><span className="font-semibold">{dq.components[k]}</span>
                        </div>
                        <Bar value={dq.components[k]} testid={`dq-bar-${k}`} />
                      </div>
                    )
                  ))}
                </div>
                {dq.drilldown?.length > 0 && (
                  <div data-testid="dq-drilldown">
                    <p className="text-xs font-semibold text-slate-700 mb-1">Cosa sistemare per migliorare la qualità dei dati</p>
                    <ul className="text-xs text-slate-600 space-y-0.5">
                      {dq.drilldown.map((d) => <li key={d.issue}>• {d.message}</li>)}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Approval (punti 41, 88, 168-170) */}
          {canApprove && ["CALCULATED", "PROVISIONAL", "UNDER_REVIEW"].includes(run.status) && (
            <Card className="border-green-200" data-testid="approval-card">
              <CardContent className="p-4">
                {!showApprove ? (
                  <button type="button" onClick={() => setShowApprove(true)}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-green-700 hover:bg-green-800 rounded-md px-4 py-2 transition-colors"
                    data-testid="approve-open-button">
                    <CheckCircle2 className="w-4 h-4" aria-hidden="true" /> Approva valutazione
                  </button>
                ) : (
                  <div className="space-y-3" data-testid="approval-form">
                    <p className="text-sm font-semibold text-slate-800">Conferma approvazione</p>
                    <ul className="text-xs text-slate-600 space-y-0.5">
                      <li>Score: <strong>{run.overall_score}/100</strong> ({run.status}) · Metodologia {run.methodology_version}</li>
                      <li>Copertura: {run.coverage_percentage}% · Data Quality: {run.data_quality_score ?? "—"}/100</li>
                      {run.status === "PROVISIONAL" && (
                        <li className="text-amber-700">Attenzione: lo score è PROVVISORIO (qualità dati sotto soglia o metriche critiche mancanti).</li>
                      )}
                    </ul>
                    <textarea value={approveNote} onChange={(e) => setApproveNote(e.target.value)}
                      placeholder="Nota professionale interna (opzionale)" rows={2} maxLength={1000}
                      className="w-full text-sm rounded-md border border-slate-300 px-3 py-2"
                      data-testid="approve-note" aria-label="Nota di approvazione" />
                    <div className="flex gap-2">
                      <button type="button" onClick={approve} disabled={busy}
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-green-700 hover:bg-green-800 disabled:opacity-50 rounded-md px-4 py-2"
                        data-testid="approve-confirm-button">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Lock className="w-4 h-4" aria-hidden="true" />}
                        Approva definitivamente
                      </button>
                      <button type="button" onClick={() => setShowApprove(false)}
                        className="text-sm font-semibold text-slate-500 px-3 py-2" data-testid="approve-cancel-button">
                        Annulla
                      </button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <DueDiligencePanel />

          {/* Storico (punti 99-101) */}
          {showHistory && history.length > 0 && (
            <Card className="border-slate-200" data-testid="score-history">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-700">Storico valutazioni</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="divide-y divide-slate-100">
                  {history.map((h) => (
                    <li key={h.id} className="py-2 flex flex-wrap items-center gap-3 text-sm"
                      data-testid={`history-${h.id}`}>
                      <span className="font-bold text-slate-900 w-14">{h.overall_score ?? "—"}</span>
                      <span className="text-xs text-slate-500">
                        Esercizio {h.period_year ?? "—"} · {new Date(h.calculated_at).toLocaleDateString("it-IT")} · metodologia {h.methodology_version}
                      </span>
                      <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${(RUN_STATUS[h.status] || RUN_STATUS.CALCULATED).cls}`}>
                        {(RUN_STATUS[h.status] || {}).label || h.status}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
